import React, { useState, useRef, useCallback, useEffect } from "react";
import "./ai-chatbot.css";
import apiService from "../../api/apiService";
import tiger0 from "../../images/tiger0.png";

const IS_ADMIN = true;

const DOMAINS = [
  {
    value: "messages",
    label: "Messages",
    short: "Messages",
    description: "Explore conversations, channels, user activity and message trends",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="10" r="1" fill="currentColor" />
        <circle cx="12" cy="10" r="1" fill="currentColor" />
        <circle cx="15" cy="10" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "pr",
    label: "Purchase Requisitions",
    short: "PR",
    description: "Analyze PR costs, approval status and procurement data",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
        <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="9" y1="17" x2="12" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

const DOMAIN_PROMPTS = {
  messages: [
    { icon: "💬", title: "Active Today", prompt: "How many active conversations are there today?" },
    { icon: "👥", title: "Top Users", prompt: "Who are the most active users this week?" },
    { icon: "📊", title: "Channel Stats", prompt: "Show me message volume per channel this week" },
    { icon: "📈", title: "Trends", prompt: "What are the messaging trends this month?" },
  ],
  pr: [
    { icon: "💰", title: "Highest Amount", prompt: "What is the reference number of the most high amount?" },
    { icon: "⏳", title: "Pending Cost", prompt: "What is the total cost of all pending PRs this month?" },
    { icon: "📋", title: "PR Summary", prompt: "Give me a summary of all purchase requisitions this month" },
    { icon: "🔍", title: "Recent PRs", prompt: "Show me the most recent purchase requisitions" },
  ],
};

const GROUP_ORDER = ["Pinned", "Today", "Yesterday", "Previous 7 days", "Older"];

const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2);

const titleFromQuestion = (question) => {
  const clean = question.replace(/\s+/g, " ").trim();
  if (clean.length <= 80) return clean || "New chat";
  return `${clean.slice(0, 77).trimEnd()}...`;
};

const conversationTime = (item) => new Date(item.lastMessageAt || item.createdAt || 0);

const sortConversations = (list) =>
  [...list].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    return conversationTime(b) - conversationTime(a);
  });

const groupConversations = (list) => {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startWeek = new Date(startToday);
  startWeek.setDate(startWeek.getDate() - 7);

  const groups = {
    Pinned: [],
    Today: [],
    Yesterday: [],
    "Previous 7 days": [],
    Older: [],
  };

  list.forEach((item) => {
    if (item.pinned) {
      groups.Pinned.push(item);
      return;
    }
    const time = conversationTime(item);
    if (time >= startToday) groups.Today.push(item);
    else if (time >= startYesterday) groups.Yesterday.push(item);
    else if (time >= startWeek) groups["Previous 7 days"].push(item);
    else groups.Older.push(item);
  });

  return GROUP_ORDER.filter((name) => groups[name].length).map((name) => ({
    name,
    items: groups[name],
  }));
};

const formatListTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  if (date >= startToday) {
    return date.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });
  }
  if (date >= startYesterday) return "Yesterday";
  return date.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
};

const parseLine = (line) =>
  line
    .split(/\*\*(.*?)\*\*/g)
    .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

const renderContent = (text) =>
  String(text || "")
    .split("\n")
    .map((line, i, arr) => (
      <React.Fragment key={i}>
        {parseLine(line)}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

const mapServerMessages = (rows) =>
  (rows || []).map((msg) => ({
    id: msg.id,
    role: msg.role === "assistant" ? "ai" : "user",
    content: msg.content || "",
    ts: new Date(msg.createdAt),
  }));

function AiChatbot({ onClose, userDetails }) {
  const [domain, setDomain] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [streamStatus, setStreamStatus] = useState("");
  const [streamingId, setStreamingId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");
  const [railOpen, setRailOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth > 900 : true,
  );
  const [activeId, setActiveId] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const turnRef = useRef(0);
  const activeIdRef = useRef(null);
  const restoredRef = useRef(false);
  const renameInputRef = useRef(null);
  const renameLockRef = useRef(false);
  const conversationsRef = useRef([]);

  conversationsRef.current = conversations;

  const userId = userDetails?.id || "";
  const storageKey = userId ? `tigerx-ai-thread:${userId}` : "";

  const rememberThread = useCallback(
    (id) => {
      if (storageKey && id) localStorage.setItem(storageKey, id);
    },
    [storageKey],
  );

  const forgetThread = useCallback(
    (id) => {
      if (storageKey && localStorage.getItem(storageKey) === id) {
        localStorage.removeItem(storageKey);
      }
    },
    [storageKey],
  );

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;
    setConversations((prev) => {
      const without = prev.filter((item) => item.id !== conversation.id);
      return sortConversations([conversation, ...without]);
    });
    if (activeIdRef.current === conversation.id) setActiveMeta(conversation);
  }, []);

  const beginThreadSwitch = useCallback(() => {
    turnRef.current += 1;
    setIsTyping(false);
    setStreamStatus("");
    setStreamingId(null);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, threadLoading]);

  useEffect(() => {
    if (renameId) renameInputRef.current?.focus();
  }, [renameId]);

  const loadConversations = useCallback(async (term = "") => {
    const result = await apiService.talk_to_ai.listConversations(term);
    if (result?.success) {
      setConversations(result.conversations || []);
      setListError("");
      return result.conversations || [];
    }
    setListError("Couldn't load chat history.");
    return null;
  }, []);

  const openConversation = useCallback(
    async (id, { closeRail = false } = {}) => {
      if (!id) return;
      beginThreadSwitch();
      const turn = turnRef.current;
      activeIdRef.current = id;
      setActiveId(id);
      setActiveMeta(conversationsRef.current.find((item) => item.id === id) || null);
      setThreadLoading(true);
      setThreadError("");
      setMessages([]);
      setInputValue("");
      setRenameId(null);
      if (closeRail && window.innerWidth <= 900) setRailOpen(false);

      const result = await apiService.talk_to_ai.getConversation(id);
      if (turnRef.current !== turn || activeIdRef.current !== id) return;

      if (!result?.success) {
        setThreadLoading(false);
        setDomain(null);
        setThreadError(result?.message || "Couldn't open this chat.");
        if (result?.message === "Conversation not found") forgetThread(id);
        return;
      }

      setDomain(result.conversation.domain);
      setMessages(mapServerMessages(result.messages));
      upsertConversation(result.conversation);
      rememberThread(id);
      setThreadLoading(false);
    },
    [beginThreadSwitch, forgetThread, rememberThread, upsertConversation],
  );

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      const items = await loadConversations(search);
      if (cancelled) return;
      setListLoading(false);
      if (!restoredRef.current && !search && items && userId) {
        restoredRef.current = true;
        const saved = storageKey ? localStorage.getItem(storageKey) : "";
        if (saved && items.some((item) => item.id === saved)) {
          openConversation(saved);
        }
      }
    }, search ? 250 : 0);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, storageKey, userId, loadConversations, openConversation]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isTyping || !domain) return;

      const turn = turnRef.current;
      const userMsgId = generateId();
      const aiMsgId = generateId();
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", content: trimmed, ts: new Date() },
      ]);
      setIsTyping(true);
      setStreamStatus("Thinking…");
      setThreadError("");

      let conversationId = activeIdRef.current;
      let answerText = "";
      let settled = false;

      // Stale events (user switched threads mid-answer) are ignored.
      const isCurrent = () =>
        turnRef.current === turn &&
        (!conversationId || activeIdRef.current === conversationId);

      const setAiContent = (content) => {
        setMessages((prev) =>
          prev.some((m) => m.id === aiMsgId)
            ? prev.map((m) => (m.id === aiMsgId ? { ...m, content } : m))
            : [...prev, { id: aiMsgId, role: "ai", content, ts: new Date() }],
        );
      };

      const handleEvent = (event, data) => {
        if (event === "conversation") {
          if (turnRef.current !== turn) return;
          if (!conversationId && data?.id) {
            conversationId = data.id;
            activeIdRef.current = conversationId;
            setActiveId(conversationId);
            rememberThread(conversationId);
          }
          upsertConversation({
            ...data,
            title:
              !data.title || data.title === "New chat"
                ? titleFromQuestion(trimmed)
                : data.title,
            preview: trimmed.slice(0, 500),
            lastMessageAt: new Date().toISOString(),
          });
          return;
        }
        if (event === "done" && data?.conversation) upsertConversation(data.conversation);
        if (!isCurrent()) return;

        if (event === "status") {
          setStreamStatus(data?.text || "");
        } else if (event === "delta") {
          answerText += data?.text || "";
          setAiContent(answerText);
          setStreamingId(aiMsgId);
        } else if (event === "reset") {
          answerText = "";
          setMessages((prev) => prev.filter((m) => m.id !== aiMsgId));
          setStreamingId(null);
        } else if (event === "done") {
          settled = true;
          setAiContent(data?.answer || answerText || "Sorry, I couldn't get a response.");
        } else if (event === "error") {
          settled = true;
          setAiContent(data?.message || "Something went wrong. Please try again.");
        }
      };

      try {
        await apiService.talk_to_ai.streamQuery(
          conversationId
            ? { question: trimmed, conversationId }
            : { question: trimmed, domain },
          handleEvent,
        );
        if (isCurrent() && !settled) {
          setAiContent(answerText || "The connection was interrupted. Please try again.");
        }
      } catch (err) {
        if (isCurrent()) {
          setAiContent(err?.message || "Something went wrong. Please try again.");
        }
      } finally {
        if (turnRef.current === turn) {
          setIsTyping(false);
          setStreamStatus("");
          setStreamingId(null);
        }
      }
    },
    [isTyping, domain, rememberThread, upsertConversation],
  );

  const handleNewChat = () => {
    beginThreadSwitch();
    activeIdRef.current = null;
    setActiveId(null);
    setActiveMeta(null);
    setDomain(null);
    setMessages([]);
    setInputValue("");
    setThreadError("");
    setThreadLoading(false);
    setRenameId(null);
  };

  const handleSelectDomain = (value) => {
    beginThreadSwitch();
    activeIdRef.current = null;
    setActiveId(null);
    setActiveMeta(null);
    setDomain(value);
    setMessages([]);
    setInputValue("");
    setThreadError("");
    setThreadLoading(false);
  };

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    sendMessage(trimmed);
    setInputValue("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  };

  const handlePromptClick = (prompt) => sendMessage(prompt);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const startRename = (event, item) => {
    event.stopPropagation();
    setRenameId(item.id);
    setRenameValue(item.title || "");
  };

  const cancelRename = () => {
    setRenameId(null);
    setRenameValue("");
  };

  const commitRename = async (id) => {
    if (renameLockRef.current) return;
    const nextTitle = renameValue.replace(/\s+/g, " ").trim();
    const current = conversations.find((item) => item.id === id);
    if (!nextTitle || nextTitle === current?.title) {
      cancelRename();
      return;
    }
    renameLockRef.current = true;
    const result = await apiService.talk_to_ai.updateConversation(id, { title: nextTitle });
    renameLockRef.current = false;
    if (result?.success && result.conversation) {
      upsertConversation(result.conversation);
      cancelRename();
      return;
    }
    setThreadError(result?.message || "Couldn't rename this chat.");
  };

  const togglePin = async (event, item) => {
    event.stopPropagation();
    const result = await apiService.talk_to_ai.updateConversation(item.id, {
      pinned: !item.pinned,
    });
    if (result?.success && result.conversation) {
      upsertConversation(result.conversation);
      return;
    }
    setListError(result?.message || "Couldn't update this chat.");
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const result = await apiService.talk_to_ai.deleteConversation(deleteTarget.id);
    setDeleting(false);
    if (!result?.success) {
      setListError(result?.message || "Couldn't delete this chat.");
      return;
    }
    setConversations((prev) => prev.filter((item) => item.id !== deleteTarget.id));
    forgetThread(deleteTarget.id);
    if (activeIdRef.current === deleteTarget.id) handleNewChat();
    setDeleteTarget(null);
  };

  const formatTime = (date) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    return value.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
  };

  if (!IS_ADMIN) return null;

  const activeDomain = DOMAINS.find((item) => item.value === domain);
  const suggestedPrompts = domain ? DOMAIN_PROMPTS[domain] : [];
  const grouped = groupConversations(conversations);
  const headerSubtitle = activeId && activeMeta?.title
    ? activeMeta.title
    : "Admin Assistant";

  return (
    <div className="ai-chatbot-panel">
      <div className="ai-chatbot__header">
        <button
          className={`ai-chatbot__action-btn${railOpen ? " ai-chatbot__action-btn--on" : ""}`}
          onClick={() => setRailOpen((open) => !open)}
          title={railOpen ? "Hide chat history" : "Show chat history"}
          aria-pressed={railOpen}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="ai-chatbot__header-bot-icon">
          <img src={tiger0} alt="Tiger X AI" className="sidebar-header__nav-icon" />
        </div>
        <div className="ai-chatbot__header-info">
          <span className="ai-chatbot__header-title">Tiger X AI</span>
          <span className="ai-chatbot__header-subtitle">{headerSubtitle}</span>
        </div>

        {activeDomain && (
          <button
            className="ai-chatbot__domain-chip"
            onClick={handleNewChat}
            title="Start a new chat in another domain"
          >
            <span className="ai-chatbot__domain-chip-label">{activeDomain.label}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m11 0h3a2 2 0 0 0 2-2v-3"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className="ai-chatbot__header-actions">
          <button className="ai-chatbot__new-chat-btn" onClick={handleNewChat} title="New chat">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <span>New</span>
          </button>
          <button className="ai-chatbot__action-btn ai-chatbot__close-btn" onClick={onClose} title="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="ai-chatbot__body">
        {railOpen && (
          <button
            className="ai-chatbot__history-backdrop"
            onClick={() => setRailOpen(false)}
            aria-label="Close chat history"
          />
        )}

        <aside className={`ai-chatbot__history${railOpen ? "" : " ai-chatbot__history--collapsed"}`}>
          <div className="ai-chatbot__history-top">
            <button className="ai-chatbot__history-new" onClick={handleNewChat}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              New chat
            </button>
            <div className="ai-chatbot__history-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                value={search}
                onChange={(e) => {
                  setListLoading(false);
                  setSearch(e.target.value);
                }}
                placeholder="Search chats"
                aria-label="Search chats"
              />
            </div>
          </div>

          <div className="ai-chatbot__history-list">
            {listLoading ? (
              <div className="ai-chatbot__history-skeletons">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="ai-chatbot__history-skeleton" />
                ))}
              </div>
            ) : listError ? (
              <div className="ai-chatbot__history-empty">
                <p>{listError}</p>
                <button type="button" onClick={() => loadConversations(search)}>
                  Try again
                </button>
              </div>
            ) : grouped.length === 0 ? (
              <div className="ai-chatbot__history-empty">
                <p>
                  {search
                    ? "No chats match your search."
                    : "No chats yet. Start a topic and it will stay here."}
                </p>
              </div>
            ) : (
              grouped.map((group) => (
                <section key={group.name} className="ai-chatbot__history-group">
                  <h3>{group.name}</h3>
                  {group.items.map((item) => {
                    const domainMeta = DOMAINS.find((entry) => entry.value === item.domain);
                    const isActive = item.id === activeId;
                    return (
                      <div
                        key={item.id}
                        className={`ai-chatbot__history-item${isActive ? " ai-chatbot__history-item--active" : ""}`}
                      >
                        {renameId === item.id ? (
                          <form
                            className="ai-chatbot__rename-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              commitRename(item.id);
                            }}
                          >
                            <input
                              ref={renameInputRef}
                              value={renameValue}
                              maxLength={120}
                              onChange={(event) => setRenameValue(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Escape") cancelRename();
                              }}
                              onBlur={() => commitRename(item.id)}
                              aria-label="Chat title"
                            />
                          </form>
                        ) : (
                          <button
                            type="button"
                            className="ai-chatbot__history-open"
                            onClick={() => openConversation(item.id, { closeRail: true })}
                          >
                            <span className="ai-chatbot__history-title-row">
                              <span className="ai-chatbot__history-title">{item.title || "New chat"}</span>
                              <span className="ai-chatbot__history-time">
                                {formatListTime(item.lastMessageAt || item.createdAt)}
                              </span>
                            </span>
                            <span className="ai-chatbot__history-meta">
                              <span className={`ai-chatbot__mini-chip ai-chatbot__mini-chip--${item.domain}`}>
                                {domainMeta?.short || item.domain}
                              </span>
                              {item.pinned && <span className="ai-chatbot__pin-dot">Pinned</span>}
                            </span>
                            {item.preview && (
                              <span className="ai-chatbot__history-preview">{item.preview}</span>
                            )}
                          </button>
                        )}
                        {renameId !== item.id && (
                          <div className="ai-chatbot__history-actions">
                            <button type="button" title={item.pinned ? "Unpin" : "Pin"} onClick={(event) => togglePin(event, item)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill={item.pinned ? "currentColor" : "none"}>
                                <path d="M14 3l7 7-4 1-4 8-2-2-5 5 1-6-2-2 8-4 1-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button type="button" title="Rename" onClick={(event) => startRename(event, item)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button type="button" title="Delete" onClick={(event) => { event.stopPropagation(); setDeleteTarget(item); }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M4 7h16M9 7V5h6v2M8 7l1 13h6l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              ))
            )}
          </div>
        </aside>

        <div className="ai-chatbot__thread">
          {!domain ? (
            <div className="ai-chatbot__domain-picker">
              <div className="ai-chatbot__domain-picker-icon-wrap">
                <div className="ai-chatbot__welcome-icon-ring" />
                <img src={tiger0} alt="Tiger X" className="ai-chatbot__welcome-icon sidebar-header__nav-icon-lg" />
              </div>
              <h2 className="ai-chatbot__domain-picker-title">Choose a Domain</h2>
              <p className="ai-chatbot__domain-picker-sub">
                Each topic keeps its own chat. What do you want to ask about?
              </p>
              <div className="ai-chatbot__domain-cards">
                {DOMAINS.map((item) => (
                  <button
                    key={item.value}
                    className={`ai-chatbot__domain-card ai-chatbot__domain-card--${item.value}`}
                    onClick={() => handleSelectDomain(item.value)}
                  >
                    <div className="ai-chatbot__domain-card-icon">{item.icon}</div>
                    <div className="ai-chatbot__domain-card-body">
                      <span className="ai-chatbot__domain-card-title">{item.label}</span>
                      <span className="ai-chatbot__domain-card-desc">{item.description}</span>
                    </div>
                    <svg className="ai-chatbot__domain-card-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="ai-chatbot__messages">
                {threadError && (
                  <div className="ai-chatbot__thread-error">{threadError}</div>
                )}
                {threadLoading ? (
                  <div className="ai-chatbot__thread-skeletons">
                    <div className="ai-chatbot__thread-skeleton ai-chatbot__thread-skeleton--user" />
                    <div className="ai-chatbot__thread-skeleton" />
                    <div className="ai-chatbot__thread-skeleton ai-chatbot__thread-skeleton--short" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="ai-chatbot__welcome">
                    <div className="ai-chatbot__welcome-icon-wrap">
                      <div className="ai-chatbot__welcome-icon-ring" />
                      <img src={tiger0} alt="Tiger X" className="ai-chatbot__welcome-icon sidebar-header__nav-icon-lg" />
                    </div>
                    <h2 className="ai-chatbot__welcome-title">Tiger X AI Assistant</h2>
                    <p className="ai-chatbot__welcome-subtitle">
                      Asking about <strong>{activeDomain.label}</strong>. This chat is saved, so you can come back to it later.
                    </p>
                    <div className="ai-chatbot__prompts-grid">
                      {suggestedPrompts.map((prompt) => (
                        <button
                          key={prompt.title}
                          className="ai-chatbot__prompt-card"
                          onClick={() => handlePromptClick(prompt.prompt)}
                          disabled={isTyping}
                        >
                          <span className="ai-chatbot__prompt-icon">{prompt.icon}</span>
                          <span className="ai-chatbot__prompt-title">{prompt.title}</span>
                          <span className="ai-chatbot__prompt-text">{prompt.prompt}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="ai-chatbot__message-list">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`ai-chatbot__message ai-chatbot__message--${msg.role}`}>
                        {msg.role === "ai" && (
                          <div className="ai-chatbot__avatar">
                            <img src={tiger0} alt="Tiger X" className="sidebar-header__nav-icon" />
                          </div>
                        )}
                        <div className="ai-chatbot__bubble-wrap">
                          <div className={`ai-chatbot__bubble ai-chatbot__bubble--${msg.role}`}>
                            <div className="ai-chatbot__bubble-content">
                              {renderContent(msg.content)}
                            </div>
                          </div>
                          <span className="ai-chatbot__ts">{formatTime(msg.ts)}</span>
                        </div>
                      </div>
                    ))}
                    {isTyping && !streamingId && (
                      <div className="ai-chatbot__message ai-chatbot__message--ai">
                        <div className="ai-chatbot__avatar">
                          <img src={tiger0} alt="Tiger X" className="sidebar-header__nav-icon" />
                        </div>
                        <div className="ai-chatbot__bubble-wrap">
                          <div className="ai-chatbot__bubble ai-chatbot__bubble--ai">
                            <div className="ai-chatbot__typing"><span /><span /><span /></div>
                            {streamStatus && <div className="ai-chatbot__status">{streamStatus}</div>}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <div className="ai-chatbot__input-area">
                <div className="ai-chatbot__input-wrap">
                  <textarea
                    ref={inputRef}
                    className="ai-chatbot__input"
                    placeholder={isTyping ? "Tiger X AI is responding…" : `Ask about ${activeDomain.label}…`}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    disabled={isTyping || threadLoading}
                  />
                  <button
                    className={`ai-chatbot__send-btn${!inputValue.trim() || isTyping || threadLoading ? " ai-chatbot__send-btn--off" : ""}`}
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isTyping || threadLoading}
                    title="Send message"
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {deleteTarget && (
        <div className="ai-chatbot__dialog-backdrop" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            className="ai-chatbot__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="ai-delete-title">Delete this chat?</h3>
            <p>
              <strong>{deleteTarget.title || "New chat"}</strong> and its messages will be removed from your history.
            </p>
            <div className="ai-chatbot__dialog-actions">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="ai-chatbot__dialog-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AiChatbot;
